import React, { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { menuData } from '../../config/menuData.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { canAccess } from '../../config/roles.js';
import './sidenav.scss';

/**
 * Off-canvas SIDE (drawer) navigation — the overflow/compact form of the shared
 * nav, grouped by module/area. Part of the unified nav system:
 *   top bar (desktop, fits) → side drawer (overflow / compact) → [bottom nav on
 *   mobile, added separately].
 *
 * - Role-filtered: reuses canAccess (config/roles.js), hides empty groups.
 * - Accessible: role="dialog"/navigation, ESC to close, focus moves into the
 *   drawer on open and returns to the trigger on close, backdrop click closes,
 *   body scroll locked while open.
 * - Active-state via NavLink (aria-current).
 */
export default function SideNav({ open, onClose, triggerRef }) {
  const { user } = useAuth();
  const panelRef = useRef(null);

  const groups = menuData.children
    .map((group) => ({
      ...group,
      visibleLeaves: (group.children || []).filter((item) => canAccess(user, item.slug)),
    }))
    .filter((group) => group.visibleLeaves.length > 0);

  // Focus management, ESC handling, and body scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused = document.activeElement;
    const trigger = triggerRef?.current;
    const firstLink = panelRef.current?.querySelector('a, button');
    firstLink?.focus();

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      // Return focus to the trigger (or whatever had it) on close.
      const target = trigger || previouslyFocused;
      target?.focus?.();
    };
  }, [open, onClose, triggerRef]);

  return (
    <>
      <div
        className={`sidenav-backdrop ${open ? 'show' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <nav
        ref={panelRef}
        className={`sidenav ${open ? 'open' : ''}`}
        aria-label="Primary"
        aria-hidden={!open}
      >
        <div className="sidenav-header d-flex align-items-center justify-content-between">
          <span className="fw-bold">
            <i className="fa fa-bars me-2" aria-hidden="true" />
            Menu
          </span>
          <button
            type="button"
            className="btn-close"
            aria-label="Close navigation"
            onClick={onClose}
          />
        </div>

        <div className="sidenav-body">
          {groups.map((group) => (
            <div className="sidenav-group" key={group.label}>
              <div className="sidenav-group-title">
                {group.icon && <i className={`${group.icon} me-2`} aria-hidden="true" />}
                {group.label}
              </div>
              <ul className="sidenav-list">
                {group.visibleLeaves.map((item) => (
                  <li key={item.slug}>
                    <NavLink
                      to={`/${item.slug}`}
                      className={({ isActive }) =>
                        `sidenav-link ${isActive ? 'active' : ''}`
                      }
                      onClick={onClose}
                    >
                      {item.icon && <i className={`${item.icon} sidenav-link-icon`} aria-hidden="true" />}
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>
    </>
  );
}
